<script setup lang="ts">
import { onMounted, onBeforeUnmount, shallowRef } from 'vue';
import ScanView from './views/ScanView.vue';
import DetailsView from './views/DetailsView.vue';
import HarnessView from './views/HarnessView.vue';
import PaywallOverlay from './components/PaywallOverlay.vue';
import { startVehicleLookupWithRegion, type VehicleLookupSession } from './composables/useVehicleLookup';
import { sanitizeRegistration } from './api/checkVehicle';
import { recordScan } from '../engine/history/db';

const harness = new URLSearchParams(location.search).get('harness') === '1';
const session = shallowRef<VehicleLookupSession | null>(null);

// Dev-only: `?mock=1` opens the details screen backed by fixture data, so it
// can be built and reviewed without a live lookup. Stripped from prod builds.
if (import.meta.env.DEV && new URLSearchParams(location.search).get('mock') === '1') {
  import('./dev/mockSession').then(({ createMockSession }) => {
    session.value = createMockSession();
  });
}

// Navigation is instant: the details screen opens on plate lock and every
// data source streams in whenever it is ready (see useVehicleLookup).
function openDetails(payload: { plate: string; region?: string }): void {
  const plate = sanitizeRegistration(payload.plate);
  // This shared entry point covers camera, manual, gallery, and details-screen
  // lookups. History is local-only and must not depend on any API succeeding.
  void recordScan({
    text: plate,
    region: payload.region,
  }).catch(() => undefined);
  session.value?.dispose();
  session.value = startVehicleLookupWithRegion(plate, payload.region);
  if (location.hash !== '#details') history.pushState({ view: 'details' }, '', '#details');
}

function closeSession(): void {
  session.value?.dispose();
  session.value = null;
}

function backToScan(): void {
  closeSession();
  if (location.hash === '#details') history.pushState({ view: 'scan' }, '', location.pathname + location.search);
}

function onPopState(): void {
  if (location.hash !== '#details') closeSession();
}

onMounted(() => window.addEventListener('popstate', onPopState));
onBeforeUnmount(() => window.removeEventListener('popstate', onPopState));
</script>

<template>
  <HarnessView v-if="harness" />
  <template v-else>
    <!-- KeepAlive keeps the scanner (and its loaded models) warm while the
         details screen is on top, so returning to scan is instant. -->
    <KeepAlive>
      <ScanView v-if="!session" @lookup="openDetails" />
    </KeepAlive>
    <DetailsView v-if="session" :session="session" @back="backToScan" @lookup="openDetails" />
  </template>
  <!-- Shown on top of everything when the device hits the free-scan quota. -->
  <PaywallOverlay />
</template>
